/**
 * 키오스크 상태 신호 (BOOTH-97) — 부팅·온/오프라인·하트비트.
 *
 * 콘솔 `/logs` 의 "지금" 패널이 이 신호로 "키오스크가 살아 있나 · 어느 버전이 돌고 있나" 를 판단한다.
 * 순수 규칙(`bootFields`·`heartbeatTick`·`bootOnce`)과 부수효과(`trackBoot`·`startHeartbeat`·
 * `installConnectivityListeners`)를 한 파일에 두지만, 테스트는 순수 쪽만 본다.
 */
import { HEARTBEAT_INTERVAL_MS, type KioskActivityInput } from "@fiket/booth-schema";
import { flushActivity, track } from "./activity";

/** 빌드 시 `vite.config.ts` 의 define 이 치환한다. 치환이 없는 환경(vitest)에서는 "dev". */
export const APP_VERSION: string = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev";

/**
 * 기기의 IANA 시간대를 읽는다 (BOOTH-78).
 * 비어 있는 문자열이거나 Intl 에러가 나면 undefined — 서버 스키마에 싣지 않는다.
 * `Intl.DateTimeFormat()` 평가 자체가 throw 하는 환경에서도 부팅을 막지 않도록
 * 생성자 호출을 try 블록 **안에서** 수행한다.
 */
export function deviceTimeZone(intl?: { resolvedOptions(): { timeZone?: string } }): string | undefined {
  try {
    const tz = (intl ?? Intl.DateTimeFormat()).resolvedOptions().timeZone;
    return typeof tz === "string" && tz.length > 0 ? tz : undefined;
  } catch {
    return undefined;
  }
}

/** userAgent 최대 길이 — 오류 메시지와 같은 한도 */
const USER_AGENT_MAX = 200;

/** 부팅 이벤트에 실을 환경 정보 — 어떤 기기·해상도·번들인지. */
export function bootFields(
  nav: { userAgent: string },
  win: { innerWidth: number; innerHeight: number },
  appVersion: string,
  tz?: string
): Pick<KioskActivityInput, "appVersion" | "userAgent" | "viewport" | "deviceTimeZone"> {
  return {
    appVersion,
    userAgent: nav.userAgent.slice(0, USER_AGENT_MAX),
    // 확대·배율에서는 innerWidth/Height 가 소수로 나온다 — 서버 VIEWPORT_RE(`\d+x\d+`) 가 배치 전체를 거부하므로 정수로
    viewport: `${Math.round(win.innerWidth)}x${Math.round(win.innerHeight)}`,
    ...(tz !== undefined ? { deviceTimeZone: tz } : {}),
  };
}

/**
 * 이번 틱에 하트비트를 넣을지 (Review Focus 3).
 * 오프라인이면 큐가 비워지지 않아 20초 하트비트가 상한 200 을 채우고 `kiosk_offline` 을 밀어낸다 —
 * 그래서 오프라인·숨김 상태에서는 아예 넣지 않는다.
 */
export function heartbeatTick(state: { hidden: boolean; onLine: boolean }): boolean {
  return !state.hidden && state.onLine;
}

/** 페이지 로드당 1회 판정 (Review Focus 4 — dev StrictMode 가 effect 를 두 번 돌린다). */
export function bootOnce(flag: { sent: boolean }): boolean {
  if (flag.sent) return false;
  flag.sent = true;
  return true;
}

const bootFlag = { sent: false };

/** 부팅 1건 — slug 가 정해진 직후 `App` 이 부른다. */
export function trackBoot(): void {
  if (typeof navigator === "undefined" || typeof window === "undefined") return;
  if (!bootOnce(bootFlag)) return;
  // 기기 시간대 포함 (BOOTH-78) — Intl 예외 시 deviceTimeZone() 이 undefined 를 반환하므로 키가 빠진다
  track("kiosk_boot", bootFields(navigator, window, APP_VERSION, deviceTimeZone()));
}

/** 20초 하트비트 — 시작 직후 1회 보내고, 반환 함수로 멈춘다. `route` 는 `withSnapshot` 이 채운다. */
export function startHeartbeat(intervalMs: number = HEARTBEAT_INTERVAL_MS): () => void {
  if (typeof window === "undefined") return () => {};
  const tick = () => {
    if (!heartbeatTick({ hidden: document.hidden, onLine: navigator.onLine })) return;
    // 틱마다 시간대를 다시 읽는다 — 켜 둔 채 시간대가 바뀌어도 다음 하트비트에 반영 (BOOTH-78)
    const tz = deviceTimeZone();
    track("kiosk_heartbeat", { appVersion: APP_VERSION, ...(tz !== undefined ? { deviceTimeZone: tz } : {}) });
  };
  tick();
  const id = window.setInterval(tick, intervalMs);
  return () => window.clearInterval(id);
}

/** 오프라인이 시작된 시각 — 복귀 이벤트의 `durationMs` 계산용 */
let offlineAt: number | null = null;

/** 온/오프라인 전환 기록 — 복귀하면 밀린 큐를 바로 보낸다. 반환 함수로 해제. */
export function installConnectivityListeners(): () => void {
  if (typeof window === "undefined") return () => {};
  const onOffline = () => {
    offlineAt = Date.now();
    track("kiosk_offline");
  };
  const onOnline = () => {
    track("kiosk_online", { durationMs: offlineAt === null ? 0 : Date.now() - offlineAt });
    offlineAt = null;
    void flushActivity();
  };
  window.addEventListener("offline", onOffline);
  window.addEventListener("online", onOnline);
  return () => {
    window.removeEventListener("offline", onOffline);
    window.removeEventListener("online", onOnline);
  };
}
